import { CommandBar, CommandBarButton, DefaultButton, DetailsList, IColumn, ICommandBarItemProps, ICommandBarStyles, IContextualMenuItem, IIconProps, IPanelStyles, Label, Link, mergeStyles, Panel, PanelType, Persona, PersonaSize, ScrollablePane, ScrollbarVisibility, SearchBox, SelectionMode, Spinner, SpinnerSize, Stack, Text, TooltipHost } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import Loader from "../../src/taskpane/components/Loader";
import { useLazyAddLibraryCurrentRoleForGroupQuery, useLazyBreakRoleInheritanceOfListQuery, useLazyDeleteLibraryCurrentRoleForGroupQuery, useLazyGetRoleDefinitionsQuery, useLazyGetSecurityGroupMembersOwnersQuery, useLazyGetSiteRoleAssignmentsQuery, useLazyRemoveUserFromSiteGroupQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { currentSharepointFolderUserPermissions } from "../../src/taskpane/redux/models/reduxModels";
import { getUserEffectivePermissions } from "../../src/taskpane/services/SharePointRestServices";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { usePopUpAuth } from "../Hooks/usePopUpAuth";
import { CMErrorModal } from "./CMErrorModal";
import { CMPermissionsAddMembersPanel, PersonaUser } from "./CMPermissionsAddMembersPanel";
import { CMPermissionsMemberListPanel } from "./CMPermissionsMemberListPanel";

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 160px)",
});

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 5,
        paddingRight: 5
    },
    navigation: {
        justifyContent: "space-between",
        marginTop: 5
    }
}

const commandBarStyle: Partial<ICommandBarStyles> = {
    root: {
        borderTop: '1px solid var(--db-border, rgb(237, 235, 233))',
        borderBottom: '1px solid var(--db-border, rgb(237, 235, 233))',
        paddingLeft: 0,
        paddingRight: 10,
        paddingBottom: 3,
        paddingTop: 3
    }
};

const addMembersIconProps: IIconProps = { iconName: "PeopleAdd" };

export interface ICMPermissionsPanelProps {
    panelIsOpen: boolean;
    sharePointListId?: string;
    closePanel: () => void;
}

export const CMPermissionsPanel = ({ panelIsOpen, sharePointListId, closePanel }: ICMPermissionsPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);

    const [commandItems, setCommandItems] = React.useState<ICommandBarItemProps[]>();
    const [isPermissionsAddMambersPanelOpen, setIsPermissionsAddMambersPanelOpen] = React.useState<boolean>(false);
    const [groupsUsers, setGroupsUsers] = React.useState<PersonaUser[]>([]);
    const [filteredUsers, setFilteredUsers] = React.useState<PersonaUser[]>([]);
    const [columns, setColumns] = React.useState<IColumn[]>([]);
    const [reloadUsers, setReloadUsers] = React.useState<boolean>(false);
    const [isLoadingUsers, setIsLoadingUsers] = React.useState<boolean>(true);
    const [roleAssignments, setRoleAssignments] = React.useState<any[]>([])
    const [busyChangingUserRole, setBusyChangingUserRole] = React.useState<boolean>(false);
    const [busyRemovingUser, setBusyRemovingUser] = React.useState<boolean>(false);
    const [firstLoad, setFirstLoad] = React.useState<boolean>(true);
    const [isPermissionsMemberListPanelOpen, setIsPermissionsMemberListPanelOpen] = React.useState<boolean>(true);
    const [selectedRoleAssignment, setSelectedRoleAssignment] = React.useState<any>();
    const [roleDefinitions, setRoleDefinitions] = React.useState<any[]>([]);
    const [groupsAndUsers, setGroupsAndUsers] = React.useState<PersonaUser[]>([]);
    const [userPermissions, setUserPermissions] = React.useState<currentSharepointFolderUserPermissions>();
    const [isCMErrorModalOpen, setIsCMErrorModalOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    const isInOneDrive = React.useRef(false);

    const [getSiteRoleAssignments] = useLazyGetSiteRoleAssignmentsQuery();
    const [breakRoleInheritanceOfList] = useLazyBreakRoleInheritanceOfListQuery();
    const [deleteLibraryCurrentRoleForGroup] = useLazyDeleteLibraryCurrentRoleForGroupQuery();
    const [addLibraryCurrentRoleForGroup] = useLazyAddLibraryCurrentRoleForGroupQuery();
    const [getRoleDefinitions] = useLazyGetRoleDefinitionsQuery();
    const [getSecurityGroupMembersOwners] = useLazyGetSecurityGroupMembersOwnersQuery();
    const [removeUserFromSiteGroup] = useLazyRemoveUserFromSiteGroupQuery();

    const { isSharePointAdminTokenAvailable, isSharePointAdminTokenRequestInProgress, requestSharePointAdminToken } = usePopUpAuth();

    React.useEffect(() => {
        if (currentDirectory) {
            isInOneDrive.current = currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder;
        }
    }, [currentDirectory?.type]);

    React.useEffect(() => {
        if (isSharePointAdminTokenAvailable) {
            (async () => {
                const userPerms = await getUserEffectivePermissions(
                    await authModule.getSharePointAccessToken(),
                    currentDirectory?.webUrl,
                    sharePointListId
                );
                if (userPerms?.canEnumeratePermissions || userPerms?.canManagePermissions) {
                    setUserPermissions(userPerms);
                    setReloadUsers(true);
                }
            })();
        }
    }, [isSharePointAdminTokenAvailable]);

    React.useEffect(() => {
        if (roleAssignments?.length > 0) {
            const _usersAndGroups: PersonaUser[] = [];
            (async () => {
                await Promise.all(
                    roleAssignments.map(async role => {
                        const roleUsers = role.Member.Users ? role.Member.Users.filter(x => x.LoginName !== 'SHAREPOINT\\system' && ((x.IsHiddenInUI === false && x.PrincipalType !== 4) || (x.PrincipalType === 4))) : [];

                        const groupItem = {
                            text: role.Member.Title,
                            secondaryText: role.Member.Email,
                            tertiaryText: role.Member.PrincipalType === 4 ? languageStrings.Group : role?.Member.Email,
                            role: role?.RoleDefinitionBindings[0]?.Name,
                            userId: role.Member.Id,
                            loginName: role.Member.LoginName,
                            email: role.Member.Email,
                            groupId: role.PrincipalId,
                            roleDefinitionId: role.RoleDefinitionBindings[0].Id,
                            roleAssignment: role,
                            canEdit: true
                        }

                        const tempUsers: PersonaUser[] = [];

                        //if (role.Member.PrincipalType !== 4) {
                        if (roleUsers.length > 0) {
                            await Promise.all(
                                roleUsers.map(async user => {
                                    if (user.PrincipalType === 4 && user.Email) {
                                        const userLoginNameParts = user.LoginName?.split('|');

                                        let groupGuid = userLoginNameParts[2] as string;
                                        let userType = "members"

                                        if (groupGuid.includes('_o')) {
                                            groupGuid = groupGuid.replace('_o', '');
                                            userType = "owners";
                                        }

                                        try {
                                            const securityUsers = await getSecurityGroupMembersOwners({
                                                siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                                                accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                                                groupGuid,
                                                userType
                                            }).unwrap();

                                            securityUsers.value.forEach(secUser => {
                                                tempUsers.push({
                                                    text: secUser.displayName,
                                                    secondaryText: secUser.mail,
                                                    tertiaryText: `${user.Title} (${userType === 'owners' ? `${languageStrings.Owner}` : `${languageStrings.Member}`})`,
                                                    groupId: role.PrincipalId,
                                                    canEdit: false,
                                                    role: role?.RoleDefinitionBindings[0]?.Name,
                                                });
                                            });

                                        } catch (error) {
                                            logger(error, "CMPermissionsPanel.tsx (ExpandSecurityGroupUsers(User))");
                                        }

                                    } else {
                                        tempUsers.push({
                                            text: user.Title,
                                            secondaryText: user.Email,
                                            role: role?.RoleDefinitionBindings[0]?.Name,
                                            tertiaryText: `${role.Member.Title}`,
                                            groupId: role.PrincipalId,
                                            canEdit: false,
                                            canRemoveFromGroup: role.Member.PrincipalType === 8 ? true : false,
                                            userId: user.Id,
                                        });
                                    }

                                })
                            )

                        }
                        // }
                        // else {
                        //     try {
                        //         const userLoginNameParts = role.Member.LoginName?.split('|');

                        //         let groupGuid = userLoginNameParts[2] as string;
                        //         let userType = "members"

                        //         if (groupGuid.includes('_o')) {
                        //             groupGuid = groupGuid.replace('_o', '');
                        //             userType = "owners";
                        //         }   

                        //         const securityUsers = await getSecurityGroupMembersOwners({
                        //             siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                        //             accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                        //             groupGuid,
                        //             userType
                        //         }).unwrap();

                        //         securityUsers.value.forEach(secUser => {
                        //             tempUsers.push({
                        //                 text: secUser.displayName,
                        //                 secondaryText: secUser.mail,
                        //                 tertiaryText: `${role.Member.Title} (${userType === 'owners' ? `${languageStrings.Owner}` : `${languageStrings.Member}`})`,
                        //                 groupId: role.PrincipalId,
                        //                 canEdit: false
                        //             });
                        //         });  

                        //     } catch (error) {
                        //         logger(error, "CMPermissionsPanel.tsx (ExpandSecurityGroupUsers(Group))");
                        //     }
                        // }


                        groupItem.tertiaryText = tempUsers.length > 0 ? `${tempUsers.length} ${tempUsers.length > 1 ? `${languageStrings.Members}` : `${languageStrings.Member}`}` : groupItem.tertiaryText;

                        _usersAndGroups.push(groupItem);
                        tempUsers.forEach(tempUser => {
                            _usersAndGroups.push(tempUser);
                        });
                    })
                )


                setGroupsAndUsers(_usersAndGroups);

                setGroupsUsers(_usersAndGroups.filter(x => x.roleAssignment));
                setFilteredUsers(_usersAndGroups.filter(x => x.roleAssignment));
                setIsLoadingUsers(false);
                setFirstLoad(false);
                setReloadUsers(false);
            })()
        }

    }, [roleAssignments]);

    React.useEffect(() => {
        if (isSharePointAdminTokenAvailable && reloadUsers === true) {
            (async () => {
                if (firstLoad) {
                    setIsLoadingUsers(true);
                }

                try {
                    const [_roleAssignments, roleDefs] = await Promise.all([
                        getSiteRoleAssignments({
                            siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                            sharePointListId
                        }).unwrap(),
                        getRoleDefinitions({
                            siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken()
                        }).unwrap()
                    ]);

                    setRoleAssignments(_roleAssignments.value);
                    setRoleDefinitions(roleDefs.value);
                } catch (error) {
                    logger(error, "CMPermissionsPanel.tsx (getSiteRoleAssignments, getRoleDefinitions)");
                }
            })()
        }
    }, [isSharePointAdminTokenAvailable, reloadUsers]);

    React.useEffect(() => {
        const items: ICommandBarItemProps[] = [];

        items.push({
            key: "addUsers",
            text: languageStrings.MembersAdd,
            disabled: busyChangingUserRole || busyRemovingUser || isLoadingUsers || userPermissions.canManagePermissions === false,
            iconProps: addMembersIconProps,
            onClick: () => {
                setIsPermissionsAddMambersPanelOpen(true)
            },
        })

        if (busyChangingUserRole) {
            items.push({
                key: "changingUser",
                text: languageStrings.UserChangingRole,
                disabled: true,
                onRenderIcon: () => {
                    return <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />
                }
            })
        }

        if (busyRemovingUser) {
            items.push({
                key: "removingIndicator",
                text: languageStrings.UserRemove,
                disabled: true,
                onRenderIcon: () => {
                    return <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />
                }
            })
        }

        setCommandItems(items);
    }, [busyChangingUserRole, busyRemovingUser, isLoadingUsers, userPermissions]);

    const onRenderSecondaryText = (item: any) => {
        return (
            <>
                {item.roleAssignment?.Member?.Users?.length > 0 && <Link onClick={() => { setIsPermissionsMemberListPanelOpen(true); setSelectedRoleAssignment(roleAssignments.find(x => x.PrincipalId === item.groupId)) }}>{item.tertiaryText}</Link>}
                {item.roleAssignment?.Member?.Users?.length === 0 && <span>No members</span>}
                {!item.roleAssignment?.Member?.Users && <span>{item.tertiaryText}</span>}
            </>
        )
    }

    React.useEffect(() => {
        const cols: IColumn[] = [{
            key: 'name',
            name: languageStrings.Name,
            minWidth: 250,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: any) => {
                return (
                    <Persona
                        text={item.text}
                        onRenderSecondaryText={() => onRenderSecondaryText(item)}
                        tertiaryText={item.secondaryText}
                        size={PersonaSize.size40}
                    />
                )
            }
        }];

        cols.push(
            {
                key: 'role',
                name: "",
                minWidth: 90,
                maxWidth: 90,
                isResizable: true,
                onRender: (item: PersonaUser) => {
                    return (
                        <>
                            <Stack verticalAlign="center" horizontalAlign="center">
                                <Stack.Item>
                                    <CommandBarButton
                                        styles={{ root: { marginTop: 7, paddingTop: 5, paddingBottom: 5 } }}
                                        text={item.role}
                                        disabled={busyChangingUserRole || busyRemovingUser || userPermissions?.canManagePermissions === false || (!item.canEdit && !item.canRemoveFromGroup) || (item.canRemoveFromGroup && sharePointListId?.length > 0)}
                                        menuProps={{
                                            items: getRoleOptions(item)
                                        }}
                                    />
                                </Stack.Item>
                            </Stack>
                        </>

                    )
                }
            }
        )

        setColumns(cols);
    }, [filteredUsers, busyChangingUserRole, busyRemovingUser, sharePointListId])

    const getRoleOptions = (item: PersonaUser) => {
        const options: IContextualMenuItem[] = [];

        if (item.canEdit) {
            roleDefinitions?.filter(x => x.Hidden === false).forEach(roledef => {
                options.push(
                    {
                        key: roledef.Id,
                        text: roledef.Name,
                        canCheck: true,
                        isChecked: item.tertiaryText === roledef.Name,
                        disabled: item.tertiaryText === roledef.Name,
                        onClick: () => {
                            onMoveUserOrGroupRole(item, roledef.Id)
                        }
                    }
                )
            });
        }


        if (item.canRemoveFromGroup || item.roleAssignment?.Member?.PrincipalType === 1 || item.roleAssignment?.Member?.PrincipalType === 4) {
            options.push(
                {
                    key: 'remove',
                    text: languageStrings.Remove, onClick: () => {
                        onRemoveUserFromSiteGroup(item);
                    }
                });
        }

        return options;
    }

    const onMoveUserOrGroupRole = async (item: PersonaUser, newRoleDefinitionId: number) => {
        setBusyChangingUserRole(true);
        if (sharePointListId) {
            try {
                await breakRoleInheritanceOfList({
                    siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                    accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                    sharePointListId
                }).unwrap();
            } catch (error) {
                logger(error, "CMPermissionsPanel.tsx (onMoveUserOrGroupRole(breakRoleInheritanceOfList))");
            }
        }

        try {
            await Promise.all([
                deleteLibraryCurrentRoleForGroup({
                    siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                    accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                    userId: item.userId,
                    sharePointListId,
                    roleDefId: item.roleDefinitionId
                }).unwrap(),
                addLibraryCurrentRoleForGroup({
                    siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                    accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                    userId: item.userId,
                    sharePointListId,
                    roleDefId: newRoleDefinitionId
                }).unwrap(),
            ])
        } catch (error) {
            logger(error, "CMPermissionsPanel.tsx (onMoveUserOrGroupRole(deleteLibraryCurrentRoleForGroup, addLibraryCurrentRoleForGroup))");

            setErrorMessage(error.data['odata.error']['message']['value']);
            setIsCMErrorModalOpen(true);
        }

        setBusyChangingUserRole(false);
        setReloadUsers(true);
    }

    const onRemoveUserFromSiteGroup = async (item: PersonaUser) => {
        setBusyRemovingUser(true);

        if (sharePointListId) {
            try {
                await breakRoleInheritanceOfList({
                    siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                    accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                    sharePointListId
                }).unwrap();
            } catch (error) {
                logger(error, "CMPermissionsPanel.tsx (onRemoveUserFromSiteGroup(breakRoleInheritanceOfList))");
            }

            try {
                const roleDefId = roleAssignments.find(x => x.PrincipalId === item.groupId)?.RoleDefinitionBindings[0]?.Id
                await deleteLibraryCurrentRoleForGroup({
                    siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                    accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                    userId: item.userId,
                    sharePointListId,
                    roleDefId
                }).unwrap();
            } catch (error) {
                logger(error, "CMPermissionsPanel.tsx (onRemoveUserFromSiteGroup(deleteLibraryCurrentRoleForGroup(if)))");
            }
        } else {
            if (item.canRemoveFromGroup) {
                try {
                    await removeUserFromSiteGroup({
                        siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                        groupId: item.groupId,
                        userId: item.userId
                    }).unwrap();
                } catch (error) {
                    logger(error, "CMPermissionsPanel.tsx (onRemoveUserFromSiteGroup(removeUserFromSiteGroup(else)))");
                }
            }
            else {
                try {
                    await deleteLibraryCurrentRoleForGroup({
                        siteUrl: isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                        userId: item.userId,
                        sharePointListId,
                        roleDefId: item.roleDefinitionId
                    }).unwrap();

                } catch (error) {
                    logger(error, "CMPermissionsPanel.tsx (onRemoveUserFromSiteGroup(deleteLibraryCurrentRoleForGroup(else)))");

                    setErrorMessage(error.data['odata.error']['message']['value']);
                    setIsCMErrorModalOpen(true);
                }
            }
        }

        setBusyRemovingUser(false);
        setReloadUsers(true);
    }

    const filterTextChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, filter?: string) => {
        if (filter?.length > 0) {
            setFilteredUsers(groupsAndUsers.filter(x => (x.text?.toLowerCase().includes(filter?.toLowerCase()) || x.secondaryText?.toLowerCase().includes(filter?.toLowerCase()) || x.tertiaryText?.toLowerCase().includes(filter?.toLowerCase()))))
        } else {
            setFilteredUsers(groupsUsers);
        }
    }

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={sharePointListId ? languageStrings.LibraryPermissions : languageStrings.SitePermissions}
            onDismiss={() => closePanel()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
        >
            {!isSharePointAdminTokenAvailable
                ? isSharePointAdminTokenRequestInProgress
                    ? <Spinner size={SpinnerSize.large} style={{ marginTop: 12 }} />
                    : <TooltipHost key="requestLink" content={languageStrings.RequestAccess}>
                        <Stack horizontal horizontalAlign="center" style={{ marginTop: 12 }}>
                            <DefaultButton
                                onClick={requestSharePointAdminToken}
                            >
                                {languageStrings.RequestAccess}
                            </DefaultButton>
                        </Stack>
                    </TooltipHost>
                : !(userPermissions?.canEnumeratePermissions || userPermissions?.canManagePermissions)
                    ? isLoadingUsers
                        ? <Spinner size={SpinnerSize.large} style={{ marginTop: 12 }} />
                        : <Stack horizontal horizontalAlign="center" style={{ marginTop: 12 }}>
                            <Text>{languageStrings.AccessDenied}</Text>
                        </Stack>
                    : (<>
                        <CommandBar
                            styles={commandBarStyle}
                            items={commandItems}
                            ariaLabel="Use left and right arrow keys to navigate between commands"
                        />

                        <div style={{ marginLeft: '10px', marginRight: '10px' }}>
                            <SearchBox
                                placeholder={languageStrings.Search}
                                onChange={filterTextChanged}
                            />
                        </div>

                        <div className={scrollStyle} >
                            <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                                <div className="xb-content">
                                    {isLoadingUsers && <Loader label={languageStrings.Loading} />}
                                    {groupsUsers?.length > 0 && !isLoadingUsers && <DetailsList
                                        items={filteredUsers}
                                        setKey="set"
                                        columns={columns}
                                        selectionMode={SelectionMode.none}
                                        ariaLabelForSelectionColumn="Toggle selection"
                                        ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                                        checkButtonAriaLabel="select row"
                                    />}
                                    {filteredUsers?.length === 0 && !isLoadingUsers &&
                                        <Stack horizontalAlign="center" styles={{ root: { paddingTop: 25 } }}>
                                            <Label disabled>{languageStrings.MembersNotFound}</Label>
                                        </Stack>
                                    }
                                    {isPermissionsAddMambersPanelOpen && <CMPermissionsAddMembersPanel
                                        panelIsOpen={isPermissionsAddMambersPanelOpen}
                                        closePanel={() => setIsPermissionsAddMambersPanelOpen(false)}
                                        roleAssignments={roleAssignments}
                                        roleDefinitions={roleDefinitions}
                                        isInOneDrive={isInOneDrive.current}
                                        closeOnSuccess={() => { setIsPermissionsAddMambersPanelOpen(false); setReloadUsers(true) }}
                                        groupsUsers={groupsUsers}
                                        sharePointListId={sharePointListId}
                                    />}
                                    {isPermissionsMemberListPanelOpen && selectedRoleAssignment && <CMPermissionsMemberListPanel
                                        panelIsOpen={isPermissionsMemberListPanelOpen}
                                        closePanel={() => setIsPermissionsMemberListPanelOpen(false)}
                                        roleAssignment={selectedRoleAssignment}
                                        users={groupsAndUsers.filter(x => x.groupId === selectedRoleAssignment.PrincipalId && !x.roleAssignment)}
                                        isInOneDrive={isInOneDrive.current}
                                        userDeleted={() => setReloadUsers(true)}
                                        sharePointListId={sharePointListId}
                                    />}
                                </div>
                            </ScrollablePane>
                        </div>
                        {isCMErrorModalOpen && <CMErrorModal message={errorMessage} showDialog={isCMErrorModalOpen} closeDialog={() => setIsCMErrorModalOpen(false)} />}
                    </>)}
        </Panel>
    )
};